import React from 'react';
import styled from 'styled-components';
import { formatBytes } from '../../../common/utils';
import { FONT_SIZES, FONT_WEIGHTS } from '../../../fundamentals/typography/typography';
import { AlertGroupName } from '../../../hooks/useAlertState/useAlertState';
import { useTestId } from '../../../hooks/useTestId/useTestId';
import { Alert } from '../../Alert/Alert';
import { ProgressBar } from '../../ProgressBar/ProgressBar';
import { Span } from '../../Texts/Texts';
import { STATUS_COLOURS } from '../../../fundamentals/colours/variants';
import { BaseDiv } from '../../../common/BaseElements';

export interface IFileProgressTranslations {
  uploading?: string;
  uploaded?: string;
}

export interface IFileProgress {
  file: File;
  progress: number;
}

export interface IFileProgressProps {
  progress?: IFileProgress[];
  translations?: IFileProgressTranslations;
}

// empty container needed as Alert has internal spacing "gap" that does not suit design needs of FileProgress
const FileProgressContainer = styled(BaseDiv)``;

const FileProgressSpan = styled(Span)<IFileProgressProps & { variant: typeof STATUS_COLOURS.SUCCESS | typeof STATUS_COLOURS.INFO }>`
  color: ${({ theme, variant }) => theme.alertColours[variant!].light.text};
`;

export const FileProgress: React.FC<IFileProgressProps> = ({ progress, translations, ...rest }) => {
  const { generateTestId } = useTestId(rest, 'FileProgress');

  const alerts = progress?.map(({ file, progress: progressValue }) => ({
    fileName: file.name,
    fileSize: file.size,
    finished: progressValue === 1,
    progressValue
  }));

  return (
    <>
      {alerts?.map(({ fileName, fileSize, finished, progressValue }) => (
        <Alert
          {...generateTestId(`Alert.${fileName}`)}
          key={fileName}
          groupName={AlertGroupName.MicroAlert}
          light
          variant={finished ? STATUS_COLOURS.SUCCESS : STATUS_COLOURS.INFO}
          id={fileName}
          gutterBottom={8}
        >
          <FileProgressContainer>
            <FileProgressSpan
              textSize={FONT_SIZES.TEXT_MICRO}
              fontWeightCss={FONT_WEIGHTS.REGULAR}
              variant={finished ? STATUS_COLOURS.SUCCESS : STATUS_COLOURS.INFO}
              {...generateTestId(`Message.${fileName}`)}
            >
              {finished ? translations?.uploaded : translations?.uploading} {fileName} ({formatBytes(fileSize)})
            </FileProgressSpan>
            <ProgressBar gutterTop={8} progress={progressValue} {...generateTestId(`Progress.${fileName}`)}></ProgressBar>
          </FileProgressContainer>
        </Alert>
      ))}
    </>
  );
};
